import { inject, Injectable } from "@angular/core";
import { GasService } from "@domain/gas/gas.service";
import { LastProductUpdateService } from "@domain/product/last-product-update.service";
import { MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductCountryService } from "@domain/product/product-country.service";
import { ProductService } from "@domain/product/product.service";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { ToastHelper } from "@utils/toast-helper.service";
import {
  catchError,
  EMPTY,
  first,
  forkJoin,
  interval,
  map,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  tap,
  throwError,
} from "rxjs";

export enum MasterDataInitStatus {
  IN_PROGRESS = "IN_PROGRESS",
  SUCCESS = "SUCCESS",
  LOGIN_FAILED = "LOGIN_FAILED",
  PRODUCT_DATA_FAILED = "PRODUCT_DATA_FAILED",
}

@Injectable({
  providedIn: "root",
})
export class MasterDataService {
  private gasService = inject(GasService);
  private toastHelper = inject(ToastHelper);
  private lastProductUpdateService = inject(LastProductUpdateService);
  private productService = inject(ProductService);
  private productCountryService = inject(ProductCountryService);
  private projectService = inject(ProjectService);
  private userService = inject(UserService);
  private monitoringProductService = inject(MonitoringProductService);

  private static readonly UPDATE_CHECK_INTERVAL_MILLIS = 30 * 60 * 1000; // 30 minutes
  private static readonly UPDATE_INTERVAL_MILLIS = 7 * 24 * 60 * 60 * 1000; // 1 week

  private initStatus = new ReplaySubject<MasterDataInitStatus>(1);

  startPeriodicUpdates() {
    this.init().subscribe();
    interval(MasterDataService.UPDATE_CHECK_INTERVAL_MILLIS).subscribe(() => {
      this.updateProductDataIfNecessary();
    });
  }

  refresh(): void {
    this.toastHelper.message($localize`:@@toast.refreshProductDataStarted:Aktualisierung der Produktdaten wurde gestartet`);

    this.userService.businessScope$
      .pipe(
        switchMap((scope) => {
          if (scope === BusinessScope.FGDS) {
            return forkJoin([this.productService.refreshAll(), this.gasService.refreshAll()]);
          } else if (scope === BusinessScope.MONITORING) {
            return this.monitoringProductService.refreshAll();
          }
          return EMPTY;
        }),
      )
      .subscribe({
        next: () =>
          this.toastHelper.success($localize`:@@toast.refreshProductDataSuccessful:Produktdaten wurden erfolgreich aktualisiert`),
        error: (e) => {
          console.error(e);
          this.toastHelper.errorOffline();
        },
      });
  }

  private init(): Observable<void> {
    this.initStatus.next(MasterDataInitStatus.IN_PROGRESS);

    this.userService.businessScope$
      .pipe(
        switchMap((scope) => {
          if (scope === BusinessScope.FGDS) {
            return forkJoin([
              this.productService.getProductCountries(),
              this.projectService.getProjectCountries(),
              this.userService.user$.pipe(
                first(),
                map((user) => user.country),
                catchError((err) => {
                  console.error(err);
                  this.initStatus.next(MasterDataInitStatus.LOGIN_FAILED);
                  return throwError(() => err);
                }),
              ),
            ]).pipe(
              tap(([productCountries, projectCountries, userCountry]) => {
                const countries = new Set<string>([...productCountries, ...projectCountries, userCountry]);
                const filteredCountries = [...countries].filter(Boolean);
                this.productCountryService.init(filteredCountries);
              }),
              switchMap(() =>
                forkJoin([this.gasService.loadAll(), this.productService.init()]).pipe(
                  map(() => {
                    this.initStatus.next(MasterDataInitStatus.SUCCESS);
                  }),
                  catchError((e) => {
                    console.error(e);
                    this.initStatus.next(MasterDataInitStatus.PRODUCT_DATA_FAILED);
                    return EMPTY;
                  }),
                ),
              ),
            );
          } else if (scope === BusinessScope.MONITORING) {
            return this.monitoringProductService.init().pipe(
              tap(() => {
                this.initStatus.next(MasterDataInitStatus.SUCCESS);
              }),
              map(() => void 0),
              catchError((e) => {
                console.error(e);
                this.initStatus.next(MasterDataInitStatus.PRODUCT_DATA_FAILED);
                return EMPTY;
              }),
            );
          }
          return of(undefined);
        }),
      )
      .subscribe();
    return of(undefined);
  }

  get initStatus$(): Observable<MasterDataInitStatus> {
    return this.initStatus.asObservable();
  }

  private updateProductDataIfNecessary() {
    this.lastProductUpdateService.getLastUpdated().subscribe((lastUpdate) => {
      const now = new Date().getTime();

      if (!navigator.onLine) {
        console.debug("Product data could not be updated because client is offline");
        return;
      }

      if (!lastUpdate || new Date(lastUpdate.date).getTime() < now - MasterDataService.UPDATE_INTERVAL_MILLIS) {
        this.refresh();
      }
    });
  }
}
